import { useEffect, useRef, useState } from 'react';
import {
  changePassword,
  clearToken,
  fetchContent,
  forgotPassword,
  getToken,
  login,
  resetPassword,
  saveContent,
  setToken,
} from './api';
import { labelFor, SECTION_LABELS } from './labels';
import { ContentContext } from '@/content';
import {
  DesignContext,
  FONTS,
  WEIGHT_LABELS,
  fontDef,
  normalizeDesign,
  resolveSectionOrder,
  type DesignContextValue,
  type TypographyOverride,
} from '@/content/design';
import Home from '../home/page';

/* ------------------------------------------------------------------ */
/* Immutable helpers                                                   */
/* ------------------------------------------------------------------ */
type Path = (string | number)[];

function setByPath(obj: any, path: Path, value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const copy = obj.slice();
    copy[head as number] = setByPath(obj[head as number], rest, value);
    return copy;
  }
  return { ...obj, [head]: setByPath(obj?.[head], rest, value) };
}

function getByPath(obj: any, path: Path): any {
  return path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

/* ------------------------------------------------------------------ */
/* Recursive field renderer (classic "Alle Texte" list view)          */
/* ------------------------------------------------------------------ */
function Field({
  label,
  value,
  path,
  onChange,
  depth,
}: {
  label: string;
  value: any;
  path: Path;
  onChange: (path: Path, value: any) => void;
  depth: number;
}) {
  if (typeof value === 'string') {
    const multiline = value.length > 55 || value.includes('\n') || value.includes('<');
    return (
      <label className="block mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            rows={Math.min(8, Math.max(2, Math.ceil(value.length / 60)))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
          />
        )}
      </label>
    );
  }

  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === 'string');
    return (
      <fieldset className="mb-5 border border-blush-100 rounded-xl p-4 bg-white">
        <legend className="px-2 text-sm font-semibold text-champagne-600">{label}</legend>
        {value.map((item, i) =>
          allStrings ? (
            <Field key={i} label={`${i + 1}.`} value={item} path={[...path, i]} onChange={onChange} depth={depth + 1} />
          ) : (
            <div key={i} className="mb-4 border-l-2 border-champagne-200 pl-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Eintrag {i + 1}</p>
              <Field label={`Eintrag ${i + 1}`} value={item} path={[...path, i]} onChange={onChange} depth={depth + 1} />
            </div>
          )
        )}
      </fieldset>
    );
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    return (
      <div className={depth === 0 ? '' : 'mb-2'}>
        {keys.map((k) => (
          <Field key={k} label={labelFor(k)} value={value[k]} path={[...path, k]} onChange={onChange} depth={depth + 1} />
        ))}
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Login screen                                                        */
/* ------------------------------------------------------------------ */
function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotMsg('');
    setBusy(true);
    try {
      const token = await login(email.trim(), password);
      setToken(token);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError('');
    setForgotMsg('');
    setForgotBusy(true);
    try {
      const sentTo = await forgotPassword();
      setForgotMsg(
        `Wir haben eine E-Mail mit einem Link zum Zurücksetzen an deine hinterlegte Adresse${
          sentTo ? ` (${sentTo})` : ''
        } gesendet. Bitte schau in dein Postfach (ggf. auch im Spam-Ordner).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'E-Mail konnte nicht gesendet werden.');
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-blush px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-blush-100 p-8">
        <div className="text-center mb-6">
          <p className="font-script text-3xl text-champagne-500">Gesichtszauber</p>
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mt-1">Bearbeitungsraum</p>
        </div>
        <form onSubmit={submit}>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">E-Mail</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Passwort</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {forgotMsg && <p className="text-sm text-green-600 mb-3">{forgotMsg}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600 transition-colors disabled:opacity-60"
          >
            {busy ? 'Anmelden …' : 'Anmelden'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={forgot}
            disabled={forgotBusy}
            className="text-sm text-gray-500 hover:text-champagne-600 underline underline-offset-2 disabled:opacity-60"
          >
            {forgotBusy ? 'E-Mail wird gesendet …' : 'Passwort vergessen?'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reset screen (via e-mail link ?reset=TOKEN)                         */
/* ------------------------------------------------------------------ */
function ResetScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < 4) {
      setError('Das neue Passwort muss mindestens 4 Zeichen haben.');
      return;
    }
    if (next !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, next);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-blush px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-blush-100 p-8">
        <div className="text-center mb-6">
          <p className="font-script text-3xl text-champagne-500">Gesichtszauber</p>
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mt-1">Neues Passwort</p>
        </div>
        {done ? (
          <div className="text-center">
            <p className="text-sm text-green-600 mb-5">
              Dein Passwort wurde geändert. Du kannst dich jetzt mit dem neuen Passwort anmelden.
            </p>
            <button
              onClick={onDone}
              className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600"
            >
              Zur Anmeldung
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label className="block mb-3">
              <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</span>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
              />
            </label>
            <label className="block mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort bestätigen</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
              />
            </label>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600 disabled:opacity-60"
            >
              {busy ? 'Speichern …' : 'Passwort speichern'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Change-password panel                                               */
/* ------------------------------------------------------------------ */
function PasswordPanel({ onClose, onExpired }: { onClose: () => void; onExpired: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMsg('');
    if (next.length < 4) {
      setError('Das neue Passwort muss mindestens 4 Zeichen haben.');
      return;
    }
    if (next !== confirm) {
      setError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setMsg('Passwort erfolgreich geändert.');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Fehler.';
      setError(m);
      if (m.includes('abgelaufen')) onExpired();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-gray-800">Passwort ändern</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Schließen">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <form onSubmit={submit}>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">Aktuelles Passwort</span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort bestätigen</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {msg && <p className="text-sm text-green-600 mb-3">{msg}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-600 rounded-full py-2 text-sm hover:bg-gray-50"
            >
              Schließen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 bg-champagne-500 text-white rounded-full py-2 text-sm font-medium hover:bg-champagne-600 disabled:opacity-60"
            >
              {busy ? 'Speichern …' : 'Ändern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Typography panel (right side, shown when an element is selected)    */
/* ------------------------------------------------------------------ */
function TypographyPanel({
  label,
  ov,
  onChange,
  onReset,
  onClose,
}: {
  label: string;
  ov: TypographyOverride;
  onChange: (patch: Partial<TypographyOverride>) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const def = fontDef(ov.fontFamily);
  const weights = def ? def.weights : [300, 400, 500, 600, 700, 800];

  const Row = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{title}</p>
      {children}
    </div>
  );

  const AlignBtn = ({ val, icon }: { val: 'left' | 'center' | 'right'; icon: string }) => (
    <button
      type="button"
      onClick={() => onChange({ textAlign: ov.textAlign === val ? undefined : val })}
      className={`flex-1 h-9 flex items-center justify-center rounded-lg border text-base ${
        ov.textAlign === val
          ? 'bg-champagne-500 text-white border-champagne-500'
          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
      }`}
    >
      <i className={icon} />
    </button>
  );

  return (
    <aside className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-80 bg-white border-l border-blush-100 shadow-xl overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-blush-100 px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Schrift bearbeiten</p>
          <p className="text-sm font-medium text-gray-800 truncate">{label}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 ml-2" aria-label="Schließen">
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      <div className="p-4">
        <Row title="Schriftart">
          <select
            value={ov.fontFamily ?? ''}
            onChange={(e) => onChange({ fontFamily: e.target.value || undefined })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-champagne-500"
          >
            <option value="">Standard</option>
            {(['Serif', 'Sans', 'Schreibschrift'] as const).map((cat) => (
              <optgroup key={cat} label={cat}>
                {FONTS.filter((f) => f.cat === cat).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </Row>

        <Row title={`Schriftgröße${ov.fontSize ? `: ${ov.fontSize}px` : ''}`}>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={10}
              max={120}
              value={ov.fontSize ?? 24}
              onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
              className="flex-1 accent-champagne-500"
            />
            <input
              type="number"
              min={8}
              max={200}
              value={ov.fontSize ?? ''}
              placeholder="–"
              onChange={(e) => onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
              className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none focus:border-champagne-500"
            />
          </div>
        </Row>

        <Row title="Schriftstärke">
          <select
            value={ov.fontWeight ?? ''}
            onChange={(e) => onChange({ fontWeight: e.target.value ? Number(e.target.value) : undefined })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-champagne-500"
          >
            <option value="">Standard</option>
            {weights.map((w) => (
              <option key={w} value={w}>
                {WEIGHT_LABELS[w] ?? w} ({w})
              </option>
            ))}
          </select>
        </Row>

        <Row title="Farbe">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={ov.color ?? '#333333'}
              onChange={(e) => onChange({ color: e.target.value })}
              className="w-10 h-9 rounded border border-gray-300 bg-white p-0.5 cursor-pointer"
            />
            <input
              type="text"
              value={ov.color ?? ''}
              placeholder="Standard"
              onChange={(e) => onChange({ color: e.target.value || undefined })}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-champagne-500"
            />
            {ov.color && (
              <button
                type="button"
                onClick={() => onChange({ color: undefined })}
                className="text-gray-400 hover:text-gray-600"
                title="Farbe zurücksetzen"
              >
                <i className="ri-close-circle-line text-lg" />
              </button>
            )}
          </div>
        </Row>

        <Row title={`Ausrichtung`}>
          <div className="flex gap-2">
            <AlignBtn val="left" icon="ri-align-left" />
            <AlignBtn val="center" icon="ri-align-center" />
            <AlignBtn val="right" icon="ri-align-right" />
          </div>
        </Row>

        <Row title={`Zeilenhöhe${ov.lineHeight ? `: ${ov.lineHeight}` : ''}`}>
          <input
            type="range"
            min={0.8}
            max={2.4}
            step={0.05}
            value={ov.lineHeight ?? 1.4}
            onChange={(e) => onChange({ lineHeight: Number(e.target.value) })}
            className="w-full accent-champagne-500"
          />
        </Row>

        <Row title={`Buchstabenabstand${typeof ov.letterSpacing === 'number' ? `: ${ov.letterSpacing}em` : ''}`}>
          <input
            type="range"
            min={-0.05}
            max={0.5}
            step={0.005}
            value={ov.letterSpacing ?? 0}
            onChange={(e) => onChange({ letterSpacing: Number(e.target.value) })}
            className="w-full accent-champagne-500"
          />
        </Row>

        <Row title="Stil">
          <button
            type="button"
            onClick={() => onChange({ italic: !ov.italic })}
            className={`h-9 px-4 rounded-lg border text-sm italic ${
              ov.italic ? 'bg-champagne-500 text-white border-champagne-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Kursiv
          </button>
        </Row>

        <button
          type="button"
          onClick={onReset}
          className="mt-2 w-full border border-gray-300 text-gray-600 rounded-full py-2 text-sm hover:bg-gray-50"
        >
          Auf Standard zurücksetzen
        </button>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Editor (authenticated)                                              */
/* ------------------------------------------------------------------ */
function EditorScreen({ onLogout }: { onLogout: () => void }) {
  const [content, setContent] = useState<any | null>(null);
  const contentRef = useRef<any>(null);
  const historyRef = useRef<any[]>([]);
  const [historyLen, setHistoryLen] = useState(0);

  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [view, setView] = useState<'visual' | 'list'>('visual');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [selected, setSelected] = useState<{ id: string; label: string } | null>(null);
  const [previewKey, setPreviewKey] = useState(0); // cache-busts the mobile <iframe> after save

  useEffect(() => {
    fetchContent()
      .then((c) => {
        contentRef.current = c;
        setContent(c);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Fehler beim Laden.'));
  }, []);

  /* --- content mutation with undo history --- */
  const applyContent = (producer: (prev: any) => any) => {
    const prev = contentRef.current;
    const next = producer(prev);
    if (next === prev) return;
    historyRef.current.push(prev);
    if (historyRef.current.length > 100) historyRef.current.shift();
    contentRef.current = next;
    setContent(next);
    setHistoryLen(historyRef.current.length);
  };

  const undo = () => {
    const prev = historyRef.current.pop();
    if (prev) {
      contentRef.current = prev;
      setContent(prev);
      setHistoryLen(historyRef.current.length);
    }
  };

  // List-view field update.
  const updateField = (path: Path, value: any) => applyContent((prev) => setByPath(prev, path, value));

  /* --- design mutators --- */
  const withDesign = (prev: any, design: any) => ({ ...prev, design });

  const setTypography = (id: string, patch: Partial<TypographyOverride>) =>
    applyContent((prev) => {
      const design = normalizeDesign(prev.design);
      const cur = design.typography[id] || {};
      const merged: any = { ...cur, ...patch };
      for (const k of Object.keys(merged)) {
        const v = merged[k];
        if (v === undefined || v === null || v === '') delete merged[k];
      }
      const typography = { ...design.typography };
      if (Object.keys(merged).length === 0) delete typography[id];
      else typography[id] = merged;
      return withDesign(prev, { ...design, typography });
    });

  const resetTypography = (id: string) =>
    applyContent((prev) => {
      const design = normalizeDesign(prev.design);
      if (!design.typography[id]) return prev;
      const typography = { ...design.typography };
      delete typography[id];
      return withDesign(prev, { ...design, typography });
    });

  const moveSectionBefore = (from: string, to: string) =>
    applyContent((prev) => {
      const design = normalizeDesign(prev.design);
      const order = resolveSectionOrder(design);
      const fromIdx = order.indexOf(from);
      if (fromIdx < 0) return prev;
      order.splice(fromIdx, 1);
      const insertAt = order.indexOf(to);
      if (insertAt < 0) order.push(from);
      else order.splice(insertAt, 0, from);
      return withDesign(prev, { ...design, order: { ...design.order, sections: order } });
    });

  const toggleHidden = (key: string) =>
    applyContent((prev) => {
      const design = normalizeDesign(prev.design);
      const hidden = { ...design.hidden };
      if (hidden[key]) delete hidden[key];
      else hidden[key] = true;
      return withDesign(prev, { ...design, hidden });
    });

  const moveListItem = (path: Path, from: number, to: number) =>
    applyContent((prev) => {
      const arr = getByPath(prev, path);
      if (!Array.isArray(arr)) return prev;
      const copy = arr.slice();
      const [m] = copy.splice(from, 1);
      copy.splice(to, 0, m);
      return setByPath(prev, path, copy);
    });

  const resetAllDesign = () => {
    if (!window.confirm('Alle Design-Änderungen (Schrift, Reihenfolge, Ausblendungen) auf Standard zurücksetzen?')) return;
    applyContent((prev) => withDesign(prev, { typography: {}, order: {}, hidden: {} }));
    setSelected(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      await saveContent(contentRef.current);
      setSaveMsg('Änderungen gespeichert. Die Webseite ist aktualisiert.');
      setPreviewKey((k) => k + 1);
      historyRef.current = [];
      setHistoryLen(0);
      window.setTimeout(() => setSaveMsg(''), 4000);
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Fehler.';
      setSaveErr(m);
      if (m.includes('abgelaufen')) onLogout();
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="p-8 text-center text-red-600">{loadError}</div>;
  if (!content) return <div className="p-8 text-center text-gray-500">Inhalte werden geladen …</div>;

  const design = normalizeDesign(content.design);
  const designCtx: DesignContextValue = {
    design,
    editMode: true,
    selectedId: selected?.id ?? null,
    selectedLabel: selected?.label ?? null,
    select: (id, label) => setSelected(id ? { id, label: label || id } : null),
    setTypography,
    resetTypography,
    moveSectionBefore,
    toggleHidden,
    moveListItem,
  };

  const selectedOv: TypographyOverride = (selected && design.typography[selected.id]) || {};

  return (
    <div className="min-h-screen bg-warm-light">
      {/* Toolbar */}
      <header className="sticky top-0 z-[70] bg-white/95 backdrop-blur border-b border-blush-100 shadow-sm">
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
          <div className="mr-2">
            <p className="font-script text-xl text-champagne-500 leading-none">Gesichtszauber</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">Bearbeitungsraum</p>
          </div>

          {/* View switch */}
          <div className="flex rounded-full border border-blush-200 overflow-hidden text-sm">
            <button
              onClick={() => setView('visual')}
              className={`px-3 py-1.5 ${view === 'visual' ? 'bg-champagne-500 text-white' : 'text-gray-600 hover:bg-blush-50'}`}
            >
              <i className="ri-brush-line mr-1" />Visuell
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 ${view === 'list' ? 'bg-champagne-500 text-white' : 'text-gray-600 hover:bg-blush-50'}`}
            >
              <i className="ri-list-check mr-1" />Alle Texte
            </button>
          </div>

          {view === 'visual' && (
            <div className="flex rounded-full border border-blush-200 overflow-hidden text-sm">
              <button
                onClick={() => setDevice('desktop')}
                className={`px-3 py-1.5 ${device === 'desktop' ? 'bg-champagne-500 text-white' : 'text-gray-600 hover:bg-blush-50'}`}
                title="Desktop-Ansicht (bearbeitbar)"
              >
                <i className="ri-computer-line" />
              </button>
              <button
                onClick={() => setDevice('mobile')}
                className={`px-3 py-1.5 ${device === 'mobile' ? 'bg-champagne-500 text-white' : 'text-gray-600 hover:bg-blush-50'}`}
                title="Handy-Vorschau (gespeicherter Stand)"
              >
                <i className="ri-smartphone-line" />
              </button>
            </div>
          )}

          <button
            onClick={undo}
            disabled={historyLen === 0}
            className="px-2.5 py-1.5 text-sm text-gray-600 hover:text-champagne-600 disabled:opacity-40"
            title="Letzte Änderung rückgängig"
          >
            <i className="ri-arrow-go-back-line mr-1" />Rückgängig
          </button>
          <button
            onClick={resetAllDesign}
            className="px-2.5 py-1.5 text-sm text-gray-500 hover:text-red-500"
            title="Alle Design-Änderungen zurücksetzen"
          >
            <i className="ri-eraser-line mr-1" />Design zurücksetzen
          </button>

          <div className="ml-auto flex items-center gap-1">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-gray-500 hover:text-champagne-500 px-2 py-1.5"
              title="Webseite in neuem Tab öffnen"
            >
              Ansehen
            </a>
            <button onClick={() => setShowPassword(true)} className="text-sm text-gray-500 hover:text-champagne-500 px-2 py-1.5">
              Passwort
            </button>
            <button onClick={onLogout} className="text-sm text-gray-500 hover:text-red-500 px-2 py-1.5">
              Abmelden
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="bg-champagne-500 text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-champagne-600 transition-colors disabled:opacity-60 shadow-md"
            >
              {saving ? 'Speichern …' : 'Speichern'}
            </button>
          </div>
        </div>

        {(saveMsg || saveErr) && (
          <div className={`px-4 py-2 text-sm text-center ${saveErr ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {saveErr || saveMsg}
          </div>
        )}
        {view === 'visual' && (
          <div className="px-4 py-1.5 text-center text-xs text-gray-500 bg-blush-50/60 border-t border-blush-100">
            <i className="ri-information-line mr-1" />
            Klicken Sie einen Text an, um Schriftart, Größe & Farbe zu ändern. Ziehen Sie
            <i className="ri-drag-move-2-line mx-1" />, um Abschnitte oder Einträge zu verschieben. Nicht vergessen: <strong>Speichern</strong>.
          </div>
        )}
      </header>

      {/* Body */}
      {view === 'list' ? (
        <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
          <p className="text-sm text-gray-500 mb-6">
            Hier bearbeiten Sie alle Texte als Liste. Mit
            <code className="mx-1 px-1 bg-blush-50 rounded">&lt;strong&gt;Wort&lt;/strong&gt;</code>
            machen Sie einzelne Wörter fett.
          </p>
          {Object.keys(content)
            .filter((k) => k !== 'design')
            .map((key) => (
              <section key={key} className="mb-6 bg-blush-50/40 rounded-2xl border border-blush-100 p-5">
                <h2 className="font-display text-xl text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-[2px] bg-champagne-400"></span>
                  {SECTION_LABELS[key] ?? labelFor(key)}
                </h2>
                <Field
                  label={SECTION_LABELS[key] ?? labelFor(key)}
                  value={content[key]}
                  path={[key]}
                  onChange={updateField}
                  depth={0}
                />
              </section>
            ))}
        </div>
      ) : device === 'mobile' ? (
        <div className="flex justify-center py-8 px-4">
          <div className="w-[390px] max-w-full">
            <div className="mb-3 text-center text-xs text-gray-500">
              Handy-Vorschau zeigt den <strong>gespeicherten</strong> Stand. Erst speichern, dann erscheinen Änderungen hier.
            </div>
            <div className="rounded-[2rem] border-8 border-gray-800 overflow-hidden shadow-2xl bg-white" style={{ height: '780px' }}>
              <iframe key={previewKey} src="/" title="Handy-Vorschau" className="w-full h-full" />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`gz-editing ${selected ? 'sm:pr-80' : ''}`}
          onClickCapture={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest && t.closest('a')) e.preventDefault();
          }}
          onClick={(e) => {
            // Click on empty canvas area deselects.
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <DesignContext.Provider value={designCtx}>
            <ContentContext.Provider value={content}>
              <Home />
            </ContentContext.Provider>
          </DesignContext.Provider>
        </div>
      )}

      {view === 'visual' && device === 'desktop' && selected && (
        <TypographyPanel
          label={selected.label}
          ov={selectedOv}
          onChange={(patch) => setTypography(selected.id, patch)}
          onReset={() => {
            resetTypography(selected.id);
          }}
          onClose={() => setSelected(null)}
        />
      )}

      {showPassword && <PasswordPanel onClose={() => setShowPassword(false)} onExpired={onLogout} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function Editor() {
  const [authed, setAuthed] = useState<boolean>(() => !!getToken());
  const [resetToken, setResetToken] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset');
  });

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (resetToken) {
    return (
      <ResetScreen
        token={resetToken}
        onDone={() => {
          window.history.replaceState({}, '', '/bearbeiten');
          clearToken();
          setAuthed(false);
          setResetToken(null);
        }}
      />
    );
  }

  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }
  return <EditorScreen onLogout={logout} />;
}
